import React, { useEffect } from 'react';
import './helpPage.css';

/**
 * HelpPage — Help & FAQ modal, opened from the app footer. Same overlay/shell
 * pattern as billing/PricingPage.js (fixed scrim, centered card, Esc closes).
 * Support contact: help@linguanewscast.com.
 */

const FAQ = [
  {
    q: 'How does LinguaNewsCast work?',
    a: 'You pick news sources and a schedule, and we generate podcast episodes from the latest '
      + 'articles — in your language, your target language, or both (great for language learning). '
      + 'Episodes appear under My Podcasts and in your private RSS feed.',
  },
  {
    q: "What's the difference between Free and Plus?",
    a: 'Free: 1 scheduled episode per day, episodes up to 30 minutes, popular preset news sources, '
      + 'episodes kept for 21 days, and a weekly sign-in requirement. Plus ($8.99/mo or $69/yr): '
      + '3 episodes per day, up to 60 minutes, your own custom news sources, no intro/outro, '
      + 'episodes kept for 120 days, and no sign-in requirement.',
  },
  {
    q: 'Why are my schedules paused?',
    a: 'Two possible reasons. On the Free plan, scheduled generation pauses if you have not signed '
      + 'in for 7 days — it resumes automatically the next time you sign in. Or, if the free tier '
      + 'was full when you joined, you are on the waitlist: your schedules are saved and start '
      + 'running as soon as a spot opens (or immediately if you upgrade to Plus).',
  },
  {
    q: 'What is the waitlist?',
    a: 'The free tier has limited capacity. When it is full, new free accounts join a queue and are '
      + 'activated in order as spots open — you will get an email when your spot is ready. '
      + 'Upgrading to Plus skips the line entirely.',
  },
  {
    q: 'Why is part of an episode in the wrong language?',
    a: 'If a translation cannot be verified as accurate, we play that sentence in the original '
      + 'language and voice rather than risk a wrong or garbled translation. A little English in a '
      + 'bilingual episode means the translator had an off moment for those lines — the rest of '
      + 'the episode is unaffected.',
  },
  {
    q: 'How do I cancel Plus?',
    a: 'Open your plan from the header, choose "Manage billing", and cancel in the billing portal. '
      + 'You keep Plus features until the end of the period you paid for. Note: if the free tier is '
      + 'full at that point, your account rejoins the waitlist.',
  },
  {
    q: 'How do I delete my account or my data?',
    a: 'Email us at help@linguanewscast.com from your account email and we will take care of it.',
  },
];

function HelpPage({ onClose }) {
  // Esc closes the modal, matching the PricingPage / SourceWizard convention.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="help-page__overlay" role="dialog" aria-modal="true" aria-label="Help and FAQ">
      <div className="help-page">
        <div className="help-page__header">
          <h2 className="help-page__title">Help &amp; FAQ</h2>
          <button type="button" className="help-page__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="help-page__body">
          {FAQ.map(({ q, a }) => (
            <details className="help-page__item" key={q}>
              <summary className="help-page__question">{q}</summary>
              <p className="help-page__answer">{a}</p>
            </details>
          ))}

          <div className="help-page__support">
            <p>
              Can&apos;t find what you need? Email us at{' '}
              <a href="mailto:help@linguanewscast.com">help@linguanewscast.com</a> — we usually
              reply within a business day.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HelpPage;
